import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export interface FixedDropdownPosition {
  top: number;
  left: number;
}

function findScrollableAncestors(el: HTMLElement | null): HTMLElement[] {
  const ancestors: HTMLElement[] = [];
  let parent = el?.parentElement ?? null;
  while (parent) {
    const { overflowY, overflowX } = getComputedStyle(parent);
    const scrollableY =
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      parent.scrollHeight > parent.clientHeight + 1;
    const scrollableX =
      (overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay') &&
      parent.scrollWidth > parent.clientWidth + 1;
    if (scrollableY || scrollableX) {
      ancestors.push(parent);
    }
    parent = parent.parentElement;
  }
  return ancestors;
}

/** Position a dropdown with `position: fixed` so it escapes overflow-hidden card shells. */
export function useFixedDropdownPosition(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  menuRef: RefObject<HTMLElement | null>,
  fallbackWidth = 220,
  onScrollClose?: () => void,
): FixedDropdownPosition | null {
  const [position, setPosition] = useState<FixedDropdownPosition | null>(null);
  const onScrollCloseRef = useRef(onScrollClose);
  onScrollCloseRef.current = onScrollClose;

  const update = useCallback(() => {
    const anchor = triggerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const gap = 4;
    const menuEl = menuRef.current;
    const menuHeight = menuEl?.offsetHeight ?? 88;
    const menuWidth = menuEl?.offsetWidth ?? fallbackWidth;
    let top = rect.bottom + gap;
    let left = rect.left;
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeight - gap);
    }
    if (left + menuWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - menuWidth - 8);
    }
    setPosition({ top, left });
  }, [fallbackWidth, menuRef, triggerRef]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    update();
    const raf = requestAnimationFrame(update);

    const handleScroll = () => {
      // Scroll events do not bubble — close instead of leaving a detached fixed menu.
      onScrollCloseRef.current?.();
    };

    window.addEventListener('resize', update);
    window.addEventListener('scroll', handleScroll);
    const scrollRoots = findScrollableAncestors(triggerRef.current);
    for (const root of scrollRoots) {
      root.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', handleScroll);
      for (const root of scrollRoots) {
        root.removeEventListener('scroll', handleScroll);
      }
    };
  }, [open, update, triggerRef]);

  return position;
}
